import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SSOAccept = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSSOUser } = useAuth();
  const [error, setError] = useState(null);
  const [returnUrl, setReturnUrl] = useState(null);

  useEffect(() => {
    const ssoToken = searchParams.get('sso_token');
    if (!ssoToken) {
      setError('Missing sso_token.');
      return;
    }

    // Strip token from URL immediately
    window.history.replaceState(null, '', window.location.pathname);

    const handleSSO = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/sso/accept`, {
          params: { sso_token: ssoToken }
        });

        const { access_token, user, redirect_to, tenant_branding } = response.data;

        // Store token and user via auth context
        setSSOUser(access_token, user, tenant_branding);

        // Redirect to the app
        navigate(redirect_to || '/admin/dashboard', { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (typeof detail === 'object' && detail?.error) {
          setError(detail.error);
          if (detail.return_url) setReturnUrl(detail.return_url);
        } else {
          setError(typeof detail === 'string' ? detail : 'SSO authentication failed.');
        }
      }
    };

    handleSSO();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-7 w-7 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Authentication Failed</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          {returnUrl ? (
            <a href={returnUrl}>
              <Button className="w-full btn-primary">Return to Hub Login</Button>
            </a>
          ) : (
            <a href="/login">
              <Button className="w-full btn-primary">Go to Login</Button>
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600 mx-auto mb-4" />
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

export default SSOAccept;
